/**
 * The quiz: from a reader's answers to two sub-series and the reasons for the
 * first. Shared by the page, which shows the result, and the Worker, which
 * writes it into a shared link's title so the link unfurls as the result and
 * not as the bare quiz.
 *
 * The questions, their options and their weights are content and belong to a
 * world: see src/shared/quizzes/. Nothing here knows a series by name.
 *
 * The design is issue #52 and the maintainer's spec of 27 September 2026. It
 * is not a personality quiz: every option points at something that happens in
 * the series it scores for, and the result says why.
 */

import type { Locale } from './locale.ts'

export type Localised = Record<Locale, string>

export interface QuizOption {
  label: Localised
  /** Points for each series slug this option speaks for. An option may score nothing. */
  scores: Readonly<Record<string, number>>
  /**
   * A clause that finishes "We chose it because …", said about the reader's
   * answer, never about the book. Null when the option scores nothing.
   */
  reason: Localised | null
}

export interface QuizQuestion {
  prompt: Localised
  options: readonly QuizOption[]
}

export interface Quiz {
  /** The world's slug. */
  world: string
  /** The lead above the questions: about this world's series, so content, not an interface string. */
  intro: Localised
  /** Every series the quiz can suggest, in the order a tie is shown. */
  series: readonly string[]
  questions: readonly QuizQuestion[]
  /**
   * Where a series starts, when its first book is not the whole answer. The
   * text has placeholders `{a}`, `{b}`… for the work slugs in `works`, which
   * the page renders as links. Otherwise the result names the series' first
   * book, with its note.
   */
  starts?: Readonly<Record<string, { text: Localised; works: readonly string[] }>>
}

/** An answer: the index of the chosen option, or null for a question left alone. */
export type Answers = readonly (number | null)[]

/** Fewer answers than this, and there is no result: the page refuses to submit. */
export const MIN_ANSWERS = 5

/**
 * The gap between first and second, in spreads, from which the result says
 * „Най-добре ви пасва X. Ако не ви звучи, пробвайте Y“ rather than „Две
 * поредици ви пасват почти еднакво“. Standardised scores can be negative,
 * so the spec's "20% of the leader" no longer means anything; half a spread
 * keeps roughly the balance it gave: 57% of full answer sets read as clear
 * (51% under the old rule), 70% of five-answer sets (68%).
 */
export const CLEAR_GAP = 0.5

/** Closer than this, two standings are the same number, computed two ways. */
const TIE_EPSILON = 1e-9

const LETTERS = 'abcdefghijklmnopqrstuvwxyz'

/** No answers at all: the quiz as it first opens. */
export function emptyAnswers(quiz: Quiz): (number | null)[] {
  return quiz.questions.map(() => null)
}

/**
 * The answers in a shared link's `?a=`: one letter per question in order,
 * `-` for a question left alone.
 *
 * The string is untrusted and is never written back into a page. Longer than
 * the quiz, or with any character that is neither `-` nor a letter some
 * question has (a–g, for seven options at most): the empty quiz, not an error.
 * A letter past the options of its own question counts as unanswered.
 */
export function parseAnswers(quiz: Quiz, raw: string | null | undefined): (number | null)[] {
  const empty = emptyAnswers(quiz)
  const letters = LETTERS.slice(0, Math.max(0, ...quiz.questions.map((question) => question.options.length)))
  if (raw === null || raw === undefined || raw.length > quiz.questions.length) return empty
  if (![...raw].every((char) => char === '-' || letters.includes(char))) return empty
  return empty.map((_, index) => {
    const char = raw[index]
    if (char === undefined || char === '-') return null
    const option = LETTERS.indexOf(char)
    return option < (quiz.questions[index]?.options.length ?? 0) ? option : null
  })
}

/** The answers as the `?a=` string: always one character per question. */
export function encodeAnswers(answers: Answers): string {
  return answers.map((answer) => (answer === null ? '-' : (LETTERS[answer] ?? '-'))).join('')
}

export function answeredCount(answers: Answers): number {
  return answers.filter((answer) => answer !== null).length
}

export interface SeriesScore {
  series: string
  /** Points from the reader's answers. */
  score: number
  /**
   * How far the score is above what answering the same questions at random
   * would give this series, in this series' own spread. What the ranking uses.
   */
  standing: number
  /**
   * False when the series could not score on any answered question: its
   * spread is zero, there is no evidence either way, and it is never named.
   */
  eligible: boolean
}

/**
 * Each series' score, ranked.
 *
 * **Standardised**, and it has to stay that way. For every answered question,
 * take the points each option gives the series, and their mean and variance
 * as if the reader chose an option at random. Over the answered questions a
 * series has an expected score (the sum of the means) and a spread (the root
 * of the summed variances). It is ranked on
 *
 *     (score − expected) / spread
 *
 * which asks how unusual the reader's answers are *for this series*.
 *
 * What it replaces, and why, so that nobody simplifies it back:
 *
 * - **Plain totals** hand the result to whoever has the most options: City
 *   Watch has 24 points available across the ten questions, Death 15. Over
 *   every full set of answers, City Watch won 24.8% of the time.
 * - **score / possible**, the spec's normalisation, fixed that and broke the
 *   other way. A series that earns its points from a few decisive options (a
 *   thin path like Gods, or Tiffany Aching) varies far more from reader to
 *   reader, and the series that varies most wins the maximum most often:
 *   Gods took 20.9%, and after its weights were lowered Tiffany took 17.8%. A
 *   reader who stopped at five answers got Tiffany 18.6% of the time.
 * - **score / (possible + k)**, additive smoothing, was measured for k = 2, 4
 *   and 6 and made it worse (the gap between most and least frequent went
 *   from 10.4 to 11.4 points). With every question answered, `possible` is
 *   the same for every reader, so the smoothing only rescales each series by
 *   a constant and drifts back towards plain totals.
 *
 * Standardised, with the spec's weights: every series between 13.0% and
 * 15.6% over all ~1M full answer sets (an even split is 14.3%), and between
 * 13.7% and 15.0% for a reader who answers only the first five. Measured 29
 * September 2026; tests/unit/quiz.test.ts holds both to a band around 14.3%.
 *
 * A series with zero spread on the answered questions could not have scored
 * whatever the reader chose. It is ranked last and marked ineligible rather
 * than divided by zero. In the Discworld quiz this cannot happen once
 * MIN_ANSWERS are answered (no series is shut out of more than four
 * questions); the rule is for the quiz of the next world.
 *
 * Ties keep the quiz's own series order, so the same answers always give the
 * same page.
 */
export function rank(quiz: Quiz, answers: Answers): SeriesScore[] {
  const scores = quiz.series.map((series) => {
    let score = 0
    let expected = 0
    let variance = 0
    quiz.questions.forEach((question, index) => {
      const answer = answers[index]
      if (answer === null || answer === undefined) return
      const points = question.options.map((option) => option.scores[series] ?? 0)
      const mean = points.reduce((sum, p) => sum + p, 0) / points.length
      expected += mean
      variance += points.reduce((sum, p) => sum + (p - mean) ** 2, 0) / points.length
      score += question.options[answer]?.scores[series] ?? 0
    })
    const eligible = variance > 0
    return { series, score, standing: eligible ? (score - expected) / Math.sqrt(variance) : Number.NEGATIVE_INFINITY, eligible }
  })
  return scores
    .map((entry, order) => ({ entry, order }))
    .sort((a, b) => b.entry.standing - a.entry.standing || a.order - b.order)
    .map(({ entry }) => entry)
}

export type QuizResult =
  | {
      /**
       * The gap between first and second, in spreads (the units `standing` is
       * in): `clear` at CLEAR_GAP or more, first then second; `close` below
       * it, almost equal, still in order; `tie` when equal, no order implied.
       */
      kind: 'clear' | 'close' | 'tie'
      first: string
      second: string
      /** The reasons for the first series: from the two or three answers that gave it the most. */
      reasons: Localised[]
      answered: number
    }
  /**
   * Enough answers, but the leading series scored no points at all. Only
   * that: a reader whose answers are spread thinly may leave every standing
   * below zero, and still gets two series, ranked, because the result
   * promises two, not one.
   */
  | { kind: 'none'; answered: number }

/** The result, or null when fewer than MIN_ANSWERS questions are answered. */
export function quizResult(quiz: Quiz, answers: Answers): QuizResult | null {
  const answered = answeredCount(answers)
  if (answered < MIN_ANSWERS) return null
  const [first, second] = rank(quiz, answers).filter((entry) => entry.eligible)
  if (first === undefined || second === undefined || first.score === 0) return { kind: 'none', answered }

  const gap = first.standing - second.standing
  const kind = gap < TIE_EPSILON ? 'tie' : gap >= CLEAR_GAP ? 'clear' : 'close'

  const reasons = quiz.questions
    .flatMap((question, index) => {
      const answer = answers[index]
      const option = answer === null || answer === undefined ? undefined : question.options[answer]
      const points = option?.scores[first.series] ?? 0
      return option?.reason && points > 0 ? [{ reason: option.reason, points, index }] : []
    })
    .sort((a, b) => b.points - a.points || a.index - b.index)
    .slice(0, 3)
    // Back in question order, so the sentence reads in the order it was asked.
    .sort((a, b) => a.index - b.index)
    .map(({ reason }) => reason)

  return { kind, first: first.series, second: second.series, reasons, answered }
}

/** "a", "a и b", "a, b и c": the reasons as one clause. */
export function joinReasons(reasons: readonly string[], and: string): string {
  if (reasons.length <= 1) return reasons[0] ?? ''
  return `${reasons.slice(0, -1).join(', ')} ${and} ${reasons[reasons.length - 1]}`
}
